import {z} from 'zod'
import {WorkspaceTypes} from 'shared/forecast/workspaceTypes'
import {licensePlans} from 'shared/forecast/license'
import {parseOrErr, zx, type NotNull} from 'shared/type'
import type {SupabaseClient} from '@supabase/supabase-js'
import type {Database} from 'utils/db'
import {err, ok, type Err, type OK} from 'shared/promise'
import {omit, pick, pipe} from 'remeda'
import {match, P} from 'ts-pattern'
import type Stripe from 'stripe'
import {env} from 'utils/env'
import {concat, notNullOrEmpty} from 'shared/collection'
import {uri} from 'shared/uri'

export const globalAdminUiStatuses = ['unrestricted', ...licensePlans, 'expired'] as const
export const zGlobalAdminUI = z.object({
  id: z.string(),
  workspace_cfg: WorkspaceTypes.zCfg.omit({domains: true, inviteLinks: true, requests: true}),
  created_at: z.string(),
  plan: z.enum([...licensePlans, 'unrestricted']),
  status: z.enum(globalAdminUiStatuses),
  cfg: WorkspaceTypes.zGlobalAdmin.nullish(),
  owner_name: zx.nullish.string,
  owner_email: zx.nullish.string,
  updated_at: zx.nullish.string
})
export type GlobalAdminUI = z.infer<typeof zGlobalAdminUI>

const zFetchedGlobalAdminUI = z.object({
  ...omit(zGlobalAdminUI.shape, ['plan', 'workspace_cfg']),
  workspace_name: z.string()
})
type FetchedGlobalAdminUI = z.infer<typeof zFetchedGlobalAdminUI>

export const fetchGlobalAdminUI = async <T extends {adminCfgRequired?: boolean} = {}>(
  sb: SupabaseClient<Database>,
  filter: {workspace_id: string} | {stripeSubscriptionId: string},
  options?: T
): Promise<
  | (T extends {adminCfgRequired: true} ? OK<{cfg: NotNull<FetchedGlobalAdminUI['cfg']>} & FetchedGlobalAdminUI>
    : OK<FetchedGlobalAdminUI>)
  | Err
> =>
  (await sb
    .from('workspace_global_admin_ui')
    .select('id, cfg, owner_name, owner_email, workspace_name:workspace_cfg->>name, created_at, status, updated_at')
    .match(
      match(filter)
        .with({workspace_id: P.string}, ({workspace_id}) => ({id: workspace_id}))
        .with({stripeSubscriptionId: P.string}, ({stripeSubscriptionId}) => ({
          'cfg->stripe->>subscriptionId': stripeSubscriptionId
        }))
        .exhaustive()
    )
    .maybeSingle()
    .then(r =>
      r.error ? err(`Failed to fetch workspace admin: ${r.error.message}`)
      : r.data == null ? err('No workspace found')
      : options?.adminCfgRequired && !r.data?.cfg ? err('No workspace global admin record found')
      : pipe(parseOrErr(zFetchedGlobalAdminUI, r.data), r => (r.ok ? ok(r.data) : r))
    )) as any

export const stripeEventsWeHandle = [
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'customer.subscription.created',
  'product.created',
  'invoice.paid',
  'invoice.overdue'
] satisfies Stripe.Event.Type[]

const stripeUri = uri(`https://dashboard.stripe.com`).with(
  env.NEXT_PUBLIC_STRIPE_KEY?.startsWith('pk_test') ? {path: ['test']} : {}
)

export const stripeUrl = (type: 'sub' | 'invoice', id: string) =>
  stripeUri.addPath(
    match(type)
      .with('sub', () => 'subscriptions')
      .with('invoice', () => 'invoices')
      .exhaustive(),
    id
  )

export const posthogUrl = (workspace_id: string) =>
  uri(`https://us.posthog.com/project/92747/replay/home`).with({
    query: {
      filters: JSON.stringify({
        filter_test_accounts: false,
        date_from: '-30d',
        date_to: null,
        filter_group: {
          type: 'AND',
          values: [
            {type: 'AND', values: [{key: 'workspace_id', value: [workspace_id], operator: 'exact', type: 'event'}]}
          ]
        },
        order: 'start_time'
      })
    }
  })

export const slackWsBlock = ({
  workspace_name,
  owner_name,
  owner_email,
  subscriptionId
}: Pick<Partial<FetchedGlobalAdminUI>, 'workspace_name' | 'owner_name' | 'owner_email'> & {
  subscriptionId?: string
}) => {
  const hasOwner = notNullOrEmpty(owner_name) || notNullOrEmpty(owner_email)
  return {
    type: 'context',
    elements: [
      {
        type: 'mrkdwn',
        text: concat(
          workspace_name && fmtSlackItem({type: 'workspace', name: workspace_name}),
          hasOwner &&
            `owned by ${concat(owner_name && `*${owner_name}*`, owner_email && `<mailto:${owner_email}|${owner_email}>`).join(' ')}`,
          subscriptionId && ` <${stripeUrl('sub', subscriptionId)}|subscription>`
        ).join('  ')
      }
    ]
  } as const
}

export const fmtSlackItem = (
  props: {type: 'workspace'; name: string} | {type: 'invoice'; number: string; id: string}
) =>
  match(props)
    .with({type: 'workspace'}, ({name}) => `🔹*${name}*`)
    .with({type: 'invoice'}, ({number, id}) => `🧾 <${stripeUrl('invoice', id)}|${number}>`)
    .exhaustive()
